import { useEffect, useState } from 'react';
import { api, ApiError, type ChatCommand, type ChatCommandInput, type BuiltinCommand } from '../lib/api';

type Permission = ChatCommandInput['permission'];
const defaultCustom: ChatCommandInput = {trigger:'!',response:'',cooldownSeconds:10,enabled:true,permission:'EVERYONE'};
const labels: Record<Permission,string> = {EVERYONE:'Alle Zuschauer',VIP:'VIPs, Mods & Streamer',MOD:'Mods & Streamer',BROADCASTER:'Nur Streamer'};
const descriptions: Record<string,string> = {
  bingo:'Spielerklärung',feld:'Feldinformationen (!feld 7)',punkte:'Punktestand',team:'Teams und sichtbare Spieler',zeit:'Verbleibende Zeit',
  challenge:'Aktuelle Challenge',vote:'Abstimmen (!vote 3)',winchallenge:'Matchliste im OBS einblenden',
};
export function Commands({lobbyId}:{lobbyId:string}) {
  const [custom,setCustom] = useState<ChatCommand[]>([]);
  const [builtin,setBuiltin] = useState<BuiltinCommand[]>([]);
  const [query,setQuery] = useState('');
  const [selected,setSelected] = useState<string|null>(null);
  const [form,setForm] = useState<ChatCommandInput>({...defaultCustom});
  const [message,setMessage] = useState('');
  const [busy,setBusy] = useState(false);
  const [loading,setLoading] = useState(true);
  async function reload(){
    try { const [a,b] = await Promise.all([api.chatCommands(lobbyId),api.builtinCommands(lobbyId)]);setCustom(a);setBuiltin(b); }
    catch(e){setMessage(e instanceof ApiError?e.message:'Commands konnten nicht geladen werden.');}
    finally {setLoading(false);}
  }
  useEffect(()=>{setLoading(true);void reload();},[lobbyId]);
  const permissionSelect = (value:Permission,onChange:(v:Permission)=>void) => (
    <select value={value} onChange={e=>onChange(e.target.value as Permission)}>
      {Object.entries(labels).map(([k,v])=><option key={k} value={k}>{v}</option>)}
    </select>
  );
  async function updateBuiltin(trigger:string, patch:Partial<Omit<BuiltinCommand,'trigger'>>) {
    setBusy(true);setMessage('');
    try { await api.builtinCommandSave(lobbyId,trigger,patch);await reload();setMessage(`!${trigger} gespeichert.`); }
    catch(e){setMessage(e instanceof ApiError?e.message:'Speichern fehlgeschlagen.');}
    finally{setBusy(false);}
  }
  async function resetBuiltin(trigger:string){
    setBusy(true);
    try{await api.builtinCommandReset(lobbyId,trigger);await reload();setMessage(`!${trigger} zurückgesetzt.`);}
    catch(e){setMessage(e instanceof ApiError?e.message:'Zurücksetzen fehlgeschlagen.');}
    finally{setBusy(false);}
  }
  function edit(c:ChatCommand){setSelected(c.id);setForm({trigger:'!'+c.trigger,response:c.response,cooldownSeconds:c.cooldownSeconds,enabled:c.enabled,permission:c.permission});}
  function fresh(){setSelected(null);setForm({...defaultCustom});}
  async function saveCustom(){
    setBusy(true);setMessage('');
    try{
      const data={...form,trigger:form.trigger.replace(/^!/, '')};
      if(selected) await api.chatCommandÄndern(lobbyId,selected,data);
      else await api.chatCommandAnlegen(lobbyId,data);
      await reload();fresh();setMessage('Command gespeichert.');
    }catch(e){setMessage(e instanceof ApiError?e.message:'Speichern fehlgeschlagen.');}
    finally{setBusy(false);}
  }
  async function deleteCustom(c:ChatCommand){
    if(!window.confirm(`!${c.trigger} löschen?`))return;
    setBusy(true);
    try{await api.chatCommandLöschen(lobbyId,c.id);await reload();if(selected===c.id)fresh();}
    catch(e){setMessage(e instanceof ApiError?e.message:'Löschen fehlgeschlagen.');}
    finally{setBusy(false);}
  }
  return <section className="panel">
    <h2>SirBotrick · Commands</h2>
    <p className="muted small">Spielbefehle und eigene Chatbefehle dieser Lobby verwalten. Änderungen gelten direkt für SirBotrick.</p>
    <input aria-label="Commands durchsuchen" placeholder="Commands durchsuchen ..." value={query} onChange={e=>setQuery(e.target.value)}/>
    <h3>Spielbefehle</h3>
    {loading?<p>Lade Commands ...</p>:builtin.filter(c=>!/^[0-9]+$/.test(c.trigger) && c.trigger !== 'vote').filter(c=>(c.trigger+' '+(descriptions[c.trigger]??'Abstimmen')+' '+(c.response??'')).toLowerCase().includes(query.toLowerCase())).map(c=><div className="panel" key={c.trigger} style={{marginBottom:10}}>
      <div className="row"><strong>!{c.trigger}</strong><span className="muted small">{descriptions[c.trigger]??'Abstimmung per Kurzform'}</span>
        <label><input type="checkbox" checked={c.enabled} disabled={busy} onChange={e=>void updateBuiltin(c.trigger,{enabled:e.target.checked})}/> Aktiv</label>
        <button type="button" className="ghost" disabled={busy} onClick={()=>void resetBuiltin(c.trigger)}>Standard</button>
      </div>
      <div className="row"><label>Cooldown (Sek.) <input type="number" min={0} max={3600} style={{width:95}} defaultValue={c.cooldownSeconds} key={c.trigger+':'+c.cooldownSeconds} onBlur={e=>{const n=Number(e.target.value);if(Number.isInteger(n)&&n>=0&&n<=3600&&n!==c.cooldownSeconds)void updateBuiltin(c.trigger,{cooldownSeconds:n});}}/></label>
        <label>Berechtigung {permissionSelect(c.permission,v=>void updateBuiltin(c.trigger,{permission:v}))}</label>
      </div>
      {!['vote','winchallenge',...Array.from({length:10},(_,i)=>String(i+1))].includes(c.trigger)&&<label>Eigene Antwort (optional, leer = Spielantwort)
        <textarea rows={2} defaultValue={c.response??''} key={c.trigger+':'+(c.response??'')} placeholder="Standardantwort verwenden" onBlur={e=>{const value=e.target.value.trim();if(value!==(c.response??''))void updateBuiltin(c.trigger,{response:value||null});}}/>
      </label>}
    </div>)}
    <h3>Eigene Commands</h3>
    <button type="button" onClick={fresh}>+ Neuer Command</button>
    {custom.filter(c=>(c.trigger+' '+c.response).toLowerCase().includes(query.toLowerCase())).map(c=><div className="panel" key={c.id} style={{marginTop:10}}>
      <div className="row"><strong>!{c.trigger}</strong><span className="muted small">{c.enabled?'Aktiv':'Deaktiviert'} · {c.cooldownSeconds}s · {labels[c.permission]}</span>
      <button type="button" onClick={()=>edit(c)}>Bearbeiten</button><button type="button" className="ghost" disabled={busy} onClick={()=>void api.chatCommandÄndern(lobbyId,c.id,{enabled:!c.enabled}).then(reload).catch(()=>setMessage('Umschalten fehlgeschlagen.'))}>{c.enabled?'Deaktivieren':'Aktivieren'}</button>
      <button type="button" className="ghost" disabled={busy} onClick={()=>void deleteCustom(c)}>Löschen</button></div><p className="muted small">{c.response}</p>
    </div>)}
    <div className="panel" style={{marginTop:15}}><h3>{selected?'Eigenen Command bearbeiten':'Neuen Command erstellen'}</h3>
      <label>Commandname<input value={form.trigger} onChange={e=>setForm(v=>({...v,trigger:e.target.value}))} placeholder="!hallo"/></label>
      <label>Antwort von SirBotrick<textarea rows={3} value={form.response} onChange={e=>setForm(v=>({...v,response:e.target.value}))}/></label>
      <p className="muted small">Platzhalter: {'{name}'}, {'{lobby}'}, {'{punkte}'}</p>
      <label>Cooldown (Sek.)<input type="number" min={0} max={3600} value={form.cooldownSeconds} onChange={e=>setForm(v=>({...v,cooldownSeconds:Number(e.target.value)}))}/></label>
      <label>Berechtigung {permissionSelect(form.permission,v=>setForm(f=>({...f,permission:v})))}</label>
      <label><input type="checkbox" checked={form.enabled} onChange={e=>setForm(v=>({...v,enabled:e.target.checked}))}/> Aktiv</label>
      <div className="row"><button type="button" className="primary" disabled={busy} onClick={()=>void saveCustom()}>{busy?'Speichert ...':'Command speichern'}</button><button type="button" className="ghost" onClick={fresh}>Zurücksetzen</button></div>
    </div>
    {message&&<p className="notice">{message}</p>}
  </section>;
}
